import {
  collection,
  doc,
  getDoc,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import type { Conversation, ConversationAnalysis } from './api';

function requireUser() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('You must be logged in to access conversations.');
  }
  return user;
}

function conversationsCollection(uid: string) {
  return collection(db, 'users', uid, 'conversations');
}

function toIsoString(value: unknown): string {
  if (value instanceof Timestamp) {
    return value.toDate().toISOString();
  }
  if (typeof value === 'string') {
    return value;
  }
  return new Date().toISOString();
}

function docToConversation(id: string, data: Record<string, unknown>): Conversation {
  const messages = (data.messages as Conversation['messages']) || [];
  const analysis = data.analysis as ConversationAnalysis | undefined;

  return {
    id,
    title: (data.title as string) || 'Untitled Conversation',
    messages,
    created_at: toIsoString(data.created_at),
    analysis: analysis || undefined,
  };
}

export async function getConversations(): Promise<Conversation[]> {
  const user = requireUser();
  const snapshot = await getDocs(conversationsCollection(user.uid));
  return snapshot.docs.map((d) => docToConversation(d.id, d.data() as Record<string, unknown>));
}

export async function getConversation(conversationId: string): Promise<Conversation> {
  const user = requireUser();
  const docRef = doc(db, 'users', user.uid, 'conversations', conversationId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    throw new Error('Conversation not found');
  }

  return docToConversation(snapshot.id, snapshot.data() as Record<string, unknown>);
}
